import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import { useParams, Redirect } from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import Iframe from "react-iframe";
import MdlSelectMultipleStudent from "../dialog/MdlSelectMultipleStudent";
import MdlSelectExam from "../dialog/MdlSelectExam";
import MdlSelectReportCard from "../dialog/MdlSelectReportCard";
import BackButton from "../ChildComponent/BackButton";

const HEADER_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;
const ACCENT = COLORS.primary_color_shade5;
const ACCENT_DARK = COLORS.primary_color_shade7;
const ACCENT_BG = COLORS.primary_color_shade0;
const ACCENT_BORDER = "rgba(3, 169, 244, 0.38)";
const ACCENT_SHADOW = "rgba(3, 169, 244, 0.28)";

const btnTransition =
  "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease, opacity 0.15s ease";

const DEFAULT_TOPPER_IFRAME_HEIGHT = "520px";
const MIN_IFRAME_PX = 240;

function getMaxIframePx() {
  if (typeof window === "undefined") return 12000;
  return Math.min(12000, Math.max(1200, (window.innerHeight || 800) * 5));
}

/** Parse postMessage payload (object or JSON string) and return a bounded pixel height, or null. */
function parseIframeHeightPx(rawData) {
  let data = rawData;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  const raw = data.iframeHeight ?? data.height;
  if (raw == null || raw === "") return null;
  const px =
    typeof raw === "number" && Number.isFinite(raw)
      ? raw
      : parseInt(String(raw).trim(), 10);
  if (!Number.isFinite(px) || px <= 0) return null;
  const maxPx = getMaxIframePx();
  return Math.min(maxPx, Math.max(MIN_IFRAME_PX, Math.round(px)));
}

const ActionButton = memo(function ActionButton({
  variant = "outline",
  selected = false,
  disabled = false,
  icon,
  children,
  onClick,
}) {
  const isPrimary = variant === "primary";

  const base = {
    width: "100%",
    padding: "6px 10px",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.15,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
    minHeight: 0,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: btnTransition,
    opacity: disabled ? 0.5 : 1,
    textAlign: "left",
  };

  let style;
  if (isPrimary) {
    style = {
      ...base,
      justifyContent: "center",
      color: COLORS.white,
      border: `1px solid ${COLORS.primary_color_shade7}`,
      background: disabled
        ? "#cbd5e1"
        : `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%)`,
      boxShadow: disabled
        ? "none"
        : `0 4px 14px ${ACCENT_SHADOW}`,
    };
  } else if (selected) {
    style = {
      ...base,
      color: ACCENT_DARK,
      border: `2px solid ${ACCENT}`,
      background: `linear-gradient(135deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.white} 100%)`,
      boxShadow: `0 4px 12px ${ACCENT_SHADOW}`,
    };
  } else {
    style = {
      ...base,
      color: "#334155",
      border: "1px solid rgba(148, 163, 184, 0.45)",
      background: COLORS.white,
      boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    };
  }

  const iconWrapStyle = {
    width: 28,
    height: 28,
    flexShrink: 0,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    background: isPrimary
      ? "rgba(255, 255, 255, 0.22)"
      : selected
      ? ACCENT
      : `linear-gradient(135deg, ${COLORS.primary_color_shade0} 0%, ${COLORS.primary_color_shade1} 100%)`,
    color: isPrimary ? COLORS.white : selected ? COLORS.white : ACCENT_DARK,
    boxShadow: isPrimary ? "none" : "inset 0 1px 0 rgba(255,255,255,0.8)",
  };

  const handleMouseEnter = (e) => {
    if (disabled) return;
    e.currentTarget.style.transform = "translateY(-2px)";
    if (isPrimary) {
      e.currentTarget.style.boxShadow = `0 8px 20px ${ACCENT_SHADOW}`;
    } else if (selected) {
      e.currentTarget.style.boxShadow = `0 6px 16px ${ACCENT_SHADOW}`;
    } else {
      e.currentTarget.style.borderColor = ACCENT;
      e.currentTarget.style.boxShadow = `0 4px 12px rgba(3, 169, 244, 0.12)`;
    }
  };

  const handleMouseLeave = (e) => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = style.boxShadow || "none";
    if (!isPrimary && !selected) {
      e.currentTarget.style.borderColor = "rgba(148, 163, 184, 0.45)";
    }
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {icon && (
        <span style={iconWrapStyle} aria-hidden>
          <i className={`fa ${icon}`} />
        </span>
      )}
      <span style={{ flex: 1, lineHeight: 1.25 }}>{children}</span>
      {!disabled && (
        <i
          className="fa fa-chevron-right"
          style={{
            fontSize: 10,
            opacity: 0.45,
            flexShrink: 0,
          }}
          aria-hidden
        />
      )}
    </button>
  );
});

const StepCard = memo(function StepCard({
  stepNumber,
  title,
  hint,
  state,
  children,
  embedded = false,
}) {
  const isLocked = state === "locked";
  return (
    <div
      style={{
        margin: embedded ? 0 : "0 16px 10px",
        height: embedded ? "100%" : undefined,
        boxSizing: "border-box",
        padding: "10px 12px",
        borderRadius: 14,
        border: `1px solid ${
          state === "active"
            ? ACCENT_BORDER
            : state === "done"
            ? "rgba(16, 185, 129, 0.35)"
            : "rgba(15,23,42,0.08)"
        }`,
        background: isLocked
          ? "#f9fafb"
          : state === "done"
          ? "linear-gradient(135deg, rgba(16,185,129,0.06) 0%, #fff 100%)"
          : COLORS.white,
        opacity: isLocked ? 0.72 : 1,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: 8,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 800,
            background:
              state === "done"
                ? "#10b981"
                : state === "active"
                ? ACCENT
                : "#e2e8f0",
            color: state === "pending" ? "#64748b" : COLORS.white,
          }}
        >
          {state === "done" ? (
            <i className="fa fa-check" style={{ fontSize: 10 }} aria-hidden />
          ) : (
            stepNumber
          )}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{title}</div>
          {hint && (
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 3, lineHeight: 1.4 }}>
              {hint}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
});

const PrintableDocsTopper = () => {
  const { document_type: documentTypeParam } = useParams();

  const loggedIn = localStorage.getItem("token") !== null;

  const [loading, setLoading] = useState(false);
  const [isOpenSelectStudent, setIsOpenSelectStudent] = useState(false);
  const [isOpenSelectExam, setIsOpenSelectExam] = useState(false);
  const [isOpenSelectReportCard, setIsOpenSelectReportCard] = useState(false);
  const [showPrintable, setShowPrintable] = useState(false);
  const [mSelectedStudent, setMSelectedStudent] = useState([]);
  const [mPrintableSamples, setMPrintableSamples] = useState([]);
  const [selectedSampleId, setSelectedSampleId] = useState("");
  const [selectedSamplePosition, setSelectedSamplePosition] = useState(0);
  const [selectionType, setSelectionType] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [previewIframeHeight, setPreviewIframeHeight] = useState(
    DEFAULT_TOPPER_IFRAME_HEIGHT
  );
  const [fileName, setFileName] = useState("");
  const [fileDirectory, setFileDirectory] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [colorOptionId, setColorOptionId] = useState("");
  const [examId, setExamId] = useState("");
  const [examName, setExamName] = useState("None");
  const [reportCardId, setReportCardId] = useState("");
  const [reportCardName, setReportCardName] = useState("None");

  const pageTitle = useMemo(() => {
    if (mPrintableSamples.length > 0 && mPrintableSamples[0].document_caption) {
      return mPrintableSamples[0].document_caption;
    }
    return "Printable Topper";
  }, [mPrintableSamples]);

  const getColorOptions = useCallback((position, samples) => {
    if (!samples || samples.length === 0) return;
    const colorOptions = samples[position].color_options || [];
    if (colorOptions.length > 0) {
      setColorOptionId(colorOptions[0].color_option_id);
    } else {
      setColorOptionId("");
    }
  }, []);

  const setSample = useCallback(
    (position, samples) => {
      if (!samples || samples.length === 0) return;
      const sample = samples[position];
      setFileName(sample.file_name);
      setFileDirectory(sample.file_directory);
      setSelectedSampleId(sample.sample_id);
      getColorOptions(position, samples);
    },
    [getColorOptions]
  );

  const buildFileUrl = useCallback(
    (overrides = {}) => {
      const dir = overrides.fileDirectory ?? fileDirectory;
      const name = overrides.fileName ?? fileName;
      const sampleId = overrides.selectedSampleId ?? selectedSampleId;
      if (!dir || !name || !sampleId) return "";

      const SCHOOL_CODE = SessionManager.getSchoolCode();
      let session_id = "";
      const mStudentIds = [];

      const students = overrides.mSelectedStudent ?? mSelectedStudent;
      const selType = overrides.selectionType ?? selectionType;

      if (selType === "student" && students.length > 0) {
        students.forEach((element, index) => {
          mStudentIds[index] = element.student_enrollment_no;
          session_id = element.session_id;
        });
      }

      return (
        process.env.REACT_APP_SERVER_NAME +
        dir +
        name +
        "?&student_ids=" +
        JSON.stringify(mStudentIds) +
        "&class_id=" +
        "&section_id=" +
        "&session_id=" +
        session_id +
        "&sample_id=" +
        sampleId +
        "&selection_type=" +
        selType +
        "&system_code=" +
        process.env.REACT_APP_SYSTEM_CODE +
        "&color_option_id=" +
        (overrides.colorOptionId ?? colorOptionId) +
        "&exam_id=" +
        (overrides.examId ?? examId) +
        "&exam_name=" +
        (overrides.examName ?? examName) +
        "&report_card_id=" +
        (overrides.reportCardId ?? reportCardId) +
        "&report_card_name=" +
        (overrides.reportCardName ?? reportCardName) +
        "&school_code=" +
        SCHOOL_CODE
      );
    },
    [
      fileDirectory,
      fileName,
      selectedSampleId,
      selectionType,
      mSelectedStudent,
      colorOptionId,
      examId,
      examName,
      reportCardId,
      reportCardName,
    ]
  );

  const buildFileUrlRef = useRef(buildFileUrl);
  buildFileUrlRef.current = buildFileUrl;

  const fetchGenRef = useRef(0);

  const replaceSampleList = useCallback(
    (data) => {
      const list = data || [];
      setMPrintableSamples(list);
      setSelectedSamplePosition(0);
      setSample(0, list);
    },
    [setSample]
  );

  const getPrintableSamples = useCallback(() => {
    const docType = documentTypeParam || documentType;
    if (!docType) return;

    const reqId = ++fetchGenRef.current;
    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      "getPrintableSample.php?";

    axios
      .get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          document_type: docType,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        if (reqId !== fetchGenRef.current) return;
        setLoading(false);
        if (response.api_status === "OK") {
          replaceSampleList(response.data);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        if (reqId !== fetchGenRef.current) return;
        setLoading(false);
        console.log("getPrintableSamples error:", error);
      });
  }, [documentTypeParam, documentType, replaceSampleList]);

  const getPrintableSubSamples = useCallback(() => {
    const docType = documentTypeParam || documentType;
    if (!docType || !selectedSampleId) return;

    const reqId = ++fetchGenRef.current;
    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      "getPrintableSubSample.php?";

    axios
      .get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          document_type: docType,
          parent_sample_id: selectedSampleId,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        if (reqId !== fetchGenRef.current) return;
        setLoading(false);
        if (response.api_status === "OK") {
          replaceSampleList(response.data);
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        if (reqId !== fetchGenRef.current) return;
        setLoading(false);
        console.log("getPrintableSubSamples error:", error);
      });
  }, [documentTypeParam, documentType, selectedSampleId, replaceSampleList]);

  useEffect(() => {
    if (documentTypeParam) {
      setDocumentType(documentTypeParam);
      getPrintableSamples();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTypeParam]);

  useEffect(() => {
    if (
      selectionType !== "student" ||
      mSelectedStudent.length === 0 ||
      !fileDirectory ||
      !fileName ||
      !selectedSampleId
    ) {
      return;
    }
    const url = buildFileUrlRef.current();
    if (!url) return;
    console.log("PrintableDocsTopper fileUrl:", url);
    setFileUrl(url);
    setShowPrintable(true);
  }, [
    mSelectedStudent,
    selectionType,
    fileDirectory,
    fileName,
    selectedSampleId,
    colorOptionId,
    examId,
    examName,
    reportCardId,
    reportCardName,
  ]);

  const changeSample = useCallback(
    (position) => {
      setSelectedSamplePosition(position);
      setSample(position, mPrintableSamples);
    },
    [mPrintableSamples, setSample]
  );

  const handleCloseSelectStudent = useCallback(() => {
    setIsOpenSelectStudent(false);
  }, []);

  const handleSelectedStudent = useCallback((selectedStudents) => {
    const students = selectedStudents || [];
    setIsOpenSelectStudent(false);
    setMSelectedStudent(students);
    setSelectionType("student");
  }, []);

  const clearStudentSelection = useCallback(() => {
    setMSelectedStudent([]);
    setSelectionType("");
    setFileUrl("");
  }, []);

  const handleCloseSelectExam = useCallback(() => {
    setIsOpenSelectExam(false);
    getPrintableSamples();
  }, [getPrintableSamples]);

  const handleSelectedExam = useCallback(
    (exam_id, exam_name) => {
      setExamId(exam_id);
      setExamName(exam_name);
      setReportCardName("None");
      setReportCardId("");
      clearStudentSelection();
      setIsOpenSelectExam(false);
      getPrintableSamples();
    },
    [getPrintableSamples, clearStudentSelection]
  );

  const handleCloseSelectReportCard = useCallback(() => {
    setIsOpenSelectReportCard(false);
  }, []);

  const handleSelectedReportCard = useCallback(
    (report_card_id, report_card_name) => {
      setReportCardId(report_card_id);
      setReportCardName(report_card_name);
      setExamName("None");
      setExamId("");
      clearStudentSelection();
      setIsOpenSelectReportCard(false);
      getPrintableSubSamples();
    },
    [getPrintableSubSamples, clearStudentSelection]
  );

  const handleIframeLoad = useCallback(() => {
    setShowPrintable(false);
  }, []);

  const trustedPrintableOrigin = useMemo(() => {
    try {
      const base = process.env.REACT_APP_SERVER_NAME || "";
      if (!base) return null;
      return new URL(base).origin;
    } catch {
      return null;
    }
  }, []);

  const handlePreviewMessage = useCallback(
    (event) => {
      if (trustedPrintableOrigin && event.origin !== trustedPrintableOrigin) {
        return;
      }
      const px = parseIframeHeightPx(event.data);
      if (px == null) return;
      setPreviewIframeHeight(`${px}px`);
    },
    [trustedPrintableOrigin]
  );

  useEffect(() => {
    window.addEventListener("message", handlePreviewMessage, false);
    return () => window.removeEventListener("message", handlePreviewMessage);
  }, [handlePreviewMessage]);

  useEffect(() => {
    setPreviewIframeHeight(DEFAULT_TOPPER_IFRAME_HEIGHT);
  }, [fileUrl]);

  const hasExam = examId && examName !== "None";
  const hasReportCard = reportCardId && reportCardName !== "None";
  const step1Done = hasExam || hasReportCard;
  const step2Done = mSelectedStudent.length > 0;

  const openSelectStudents = useCallback(() => {
    if (!step1Done) {
      myToast.notify_warning(
        "Please select an Exam or Report Card first (Step 1)."
      );
      return;
    }
    setIsOpenSelectStudent(true);
  }, [step1Done]);

  const openSelectExam = useCallback(() => setIsOpenSelectExam(true), []);
  const openSelectReportCard = useCallback(
    () => setIsOpenSelectReportCard(true),
    []
  );

  const onSampleStripClick = useCallback(
    (e) => {
      const raw = e.currentTarget.getAttribute("data-sample-index");
      if (raw == null) return;
      const index = Number.parseInt(raw, 10);
      if (Number.isNaN(index)) return;
      changeSample(index);
    },
    [changeSample]
  );

  const onSampleStripKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      onSampleStripClick(e);
    },
    [onSampleStripClick]
  );

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (loading && mPrintableSamples.length === 0) {
    return <MyLoader />;
  }

  return (
    <div className="content" style={{ background: COLORS.white }}>
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="#" style={{ color: COLORS.white }}>
                {pageTitle}
              </a>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "0",
          padding: 0,
          overflow: "hidden",
          borderRadius: 14,
        }}
      >
        <div
          className="header-section"
          style={{
            background: HEADER_GRADIENT,
            color: "#fff",
            padding: "10px 16px",
            margin: 0,
            borderRadius: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 10,
            minHeight: 56,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "999px",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa fa-trophy" style={{ fontSize: 18 }} />
            </div>
            <div>
              <h5 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {pageTitle}
              </h5>
              <small style={{ display: "block", marginTop: 2, fontSize: 12, opacity: 0.92 }}>
                Follow the steps: Exam or Report Card, then Students.
              </small>
            </div>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 12,
            margin: "12px 16px 0",
            alignItems: "stretch",
          }}
        >
        <StepCard
          embedded
          stepNumber={1}
          title="Select Exam or Report Card"
          hint="For one exam, choose Exam. For multiple exams combined, choose Report Card."
          state={step1Done ? "done" : "active"}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch",
              gap: 8,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <ActionButton
                icon="fa-calendar-check-o"
                selected={hasExam}
                onClick={openSelectExam}
              >
                {hasExam ? "Change Exam" : "Select Exam"}
              </ActionButton>
            </div>
            <span
              style={{
                alignSelf: "center",
                flexShrink: 0,
                fontSize: 10,
                fontWeight: 800,
                color: "#94a3b8",
                textTransform: "uppercase",
                letterSpacing: 0.5,
                padding: "0 2px",
              }}
              aria-hidden
            >
              or
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <ActionButton
                icon="fa-file-text-o"
                selected={hasReportCard}
                onClick={openSelectReportCard}
              >
                {hasReportCard ? "Change Report Card" : "Select Report Card"}
              </ActionButton>
            </div>
          </div>
          {step1Done && (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 12,
                background:
                  "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(3,169,244,0.08) 100%)",
                border: "1px solid rgba(16, 185, 129, 0.35)",
                fontSize: 12,
                fontWeight: 600,
                color: "#047857",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: "#10b981",
                  color: COLORS.white,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                <i className="fa fa-check" aria-hidden />
              </span>
              <span style={{ lineHeight: 1.35 }}>
                {hasExam ? `Exam: ${examName}` : `Report card: ${reportCardName}`}
              </span>
            </div>
          )}
        </StepCard>

        <StepCard
          embedded
          stepNumber={2}
          title="Select Students"
          hint={step1Done ? "Pick students for the exam or report card you selected." : "Complete Step 1 before selecting students."}
          state={!step1Done ? "locked" : step2Done ? "done" : "active"}
        >
          <ActionButton
            variant="primary"
            icon="fa-users"
            disabled={!step1Done}
            onClick={openSelectStudents}
          >
            {step2Done
              ? `Change Students (${mSelectedStudent.length})`
              : "Select Students"}
          </ActionButton>
        </StepCard>
        </div>

        <div style={{ padding: "8px 16px 12px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa fa-clone" style={{ color: ACCENT, fontSize: 14 }} />
              <h5
                style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#374151",
                }}
              >
                Sample List
              </h5>
            </div>
            <span style={{ fontSize: 11, color: "#6b7280" }}>
              {mPrintableSamples.length > 0
                ? `${mPrintableSamples.length} template${mPrintableSamples.length > 1 ? "s" : ""}`
                : "No templates configured"}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              paddingBottom: 4,
              minHeight: 72,
            }}
          >
            {loading && (
              <div style={{ width: "100%", textAlign: "center", padding: 16 }}>
                <MyLoader />
              </div>
            )}
            {!loading &&
              mPrintableSamples.map((item, index) => {
                const isActive = selectedSamplePosition === index;
                return (
                  <div key={item.sample_id || index} style={{ width: 120 }}>
                    <div
                      role="button"
                      tabIndex={0}
                      data-sample-index={index}
                      onClick={onSampleStripClick}
                      onKeyDown={onSampleStripKeyDown}
                      className="cursor"
                      style={{
                        borderRadius: 8,
                        padding: 4,
                        border: isActive
                          ? `2px solid ${ACCENT}`
                          : "2px solid #d1d5db",
                        boxShadow: isActive
                          ? `0 8px 18px ${ACCENT_SHADOW}`
                          : "0 4px 10px rgba(15,23,42,0.06)",
                        background: isActive ? ACCENT_BG : "#ffffff",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                        cursor: "pointer",
                        position: "relative",
                      }}
                    >
                      {isActive && (
                        <div
                          style={{
                            position: "absolute",
                            top: 6,
                            right: 6,
                            width: 18,
                            height: 18,
                            borderRadius: "999px",
                            background: ACCENT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i className="fa fa-check" style={{ color: "#fff", fontSize: 10 }} />
                        </div>
                      )}
                      <div
                        style={{
                          borderRadius: 8,
                          overflow: "hidden",
                          background: "#f9fafb",
                          height: 40,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 2,
                        }}
                      >
                        <i
                          className="fa fa-trophy"
                          style={{ fontSize: 22, color: isActive ? ACCENT : "#9ca3af" }}
                          aria-hidden
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#111827",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={item.sample_name}
                      >
                        {item.sample_name}
                      </div>
                    </div>
                  </div>
                );
              })}
            {!loading && mPrintableSamples.length === 0 && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 8,
                  background: "#f9fafb",
                  fontSize: 12,
                  color: "#6b7280",
                  width: "100%",
                }}
              >
                No topper templates are configured for this document type.
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: "4px 16px 14px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa fa-eye" style={{ fontSize: 14, color: ACCENT_DARK }} />
              <h5
                style={{
                  margin: 0,
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "#374151",
                }}
              >
                Preview
              </h5>
            </div>
            {!fileUrl && (
              <span style={{ fontSize: 11, color: "#9ca3af" }}>
                Select a template to load preview
              </span>
            )}
          </div>

          <div
            style={{
              position: "relative",
              borderRadius: 12,
              overflow: "hidden",
              border: "1px solid rgba(15,23,42,0.08)",
              background: "#f9fafb",
            }}
          >
            {showPrintable && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(to bottom, rgba(249,250,251,0.9), rgba(243,244,246,0.95))",
                  zIndex: 10,
                }}
              >
                <MyLoader />
              </div>
            )}
            <Iframe
              url={fileUrl}
              width="100%"
              height={previewIframeHeight}
              overflow="visible"
              id="iFrameTopperPreview"
              className="bg_card_primary_outline"
              display="initial"
              position="relative"
              onLoad={handleIframeLoad}
            />
          </div>
        </div>
      </div>

      {isOpenSelectStudent && (
        <MdlSelectMultipleStudent
          actionClose={handleCloseSelectStudent}
          actionHandleSelected={handleSelectedStudent}
        />
      )}
      {isOpenSelectExam && (
        <MdlSelectExam
          action_close={handleCloseSelectExam}
          action_select={handleSelectedExam}
        />
      )}
      {isOpenSelectReportCard && (
        <MdlSelectReportCard
          action_close={handleCloseSelectReportCard}
          action_select={handleSelectedReportCard}
        />
      )}
    </div>
  );
};

export default PrintableDocsTopper;
